import { 
  cartAdd, cartGetQuantityOfVariant,
  Variant, printMoney, t
} from '@process-creative/slate-theme-tools';
import { EVT_VARIANT_CHANGED } from './variant-selector';
import { CLASS_DISABLED, CLASS_ACTIVE, CLASS_VISIBLE } from '../../tools/constants/classes';

const SELECTOR_STICKY_SIZE_SELECTOR = '[data-sticky-size-selector]';
const SELECTOR_STICKY_ADD_BTN = '[data-sticky-add-to-cart]';
const SELECTOR_STICKY_NOTIFY_BTN = '[data-notify]';
const SELECTOR_STICKY_PRICE = '[data-sticky-price]';
const SELECTOR_MAIN_ADD_BTN = '[data-product-form-add]';
const SELECTOR_MAIN_ADD_BTN_WRAPPER = '[data-add-btn-wrapper]';

export class StickyAddToCart {
  private container: HTMLElement;
  private sizeSelector: HTMLSelectElement | null = null;
  private addBtn: HTMLButtonElement | null = null;
  private notifyBtn: HTMLButtonElement | null = null;
  private priceContainer: HTMLElement | null = null;
  private mainAddBtn: HTMLElement | null = null;
  private mainAddBtnWrapper: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private productForm: HTMLFormElement | null = null;
  private currentVariant: Variant | null = null;
  private adding = false;
  private hasSeenMainForm = false;
  private mainFormBottom = 0;
  private footerElement: HTMLElement | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    this.queryElements();
    this.attachEvents();
    this.initializeIntersectionObserver();
    this.initializeButtonState();
  }

  private queryElements() {
    this.sizeSelector = this.container.querySelector(SELECTOR_STICKY_SIZE_SELECTOR);
    this.addBtn = this.container.querySelector(SELECTOR_STICKY_ADD_BTN);
    this.notifyBtn = this.container.querySelector(SELECTOR_STICKY_NOTIFY_BTN);
    this.priceContainer = this.container.querySelector(SELECTOR_STICKY_PRICE);
    this.mainAddBtn = document.querySelector(SELECTOR_MAIN_ADD_BTN);
    this.mainAddBtnWrapper = document.querySelector(SELECTOR_MAIN_ADD_BTN_WRAPPER);
    this.productForm = document.querySelector('[data-product-form]');
    this.footerElement = document.querySelector('[data-footer]');
  }

  private attachEvents() {
    
    if (this.productForm) {
      this.productForm.addEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
    }

    if (this.sizeSelector) {
      this.sizeSelector.addEventListener('change', this.onSizeChange);
    } 

    if (this.addBtn) {
      this.addBtn.addEventListener('click', this.onAddToCart);
    }

    window.addEventListener('scroll', this.onScroll);
    window.addEventListener('resize', this.onResize);
  }

  private initializeIntersectionObserver() {
    const targetElement = this.mainAddBtn;
    if (!targetElement) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Main button is visible - hide sticky
            this.hideStickyCart();
          } else {
            // Main button is not visible - show sticky (if not at footer)
            if (!this.shouldHideForFooter()) {
              this.showStickyCart();
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0
      }
    );

    this.intersectionObserver.observe(targetElement);
  }

  private calculateMainFormBottom() {
    // Use wrapper if available, fallback to button
    const targetElement = this.mainAddBtnWrapper || this.mainAddBtn;
    if (!targetElement) return;
    
    const rect = targetElement.getBoundingClientRect();
    this.mainFormBottom = window.scrollY + rect.bottom;
  }

  private checkInitialPosition() {
    // Use wrapper if available, fallback to button
    const targetElement = this.mainAddBtnWrapper || this.mainAddBtn;
    if (!targetElement) return;

    const rect = targetElement.getBoundingClientRect();
    const isMainFormHidden = targetElement.style.display === 'none' || 
                            targetElement.offsetHeight === 0;
    const isMainFormOutOfView = rect.bottom < 0;

    if (isMainFormHidden || isMainFormOutOfView) {
      this.hasSeenMainForm = true;
      this.calculateMainFormBottom();
      this.showStickyCart();
    }
  }

  private initializeButtonState() {
    if (!this.productForm) return;

    const variantSelector = this.productForm.querySelector('[name="id"]') as HTMLInputElement;
    if (!variantSelector) return;

    const variantId = parseInt(variantSelector.value);
    
    if (!variantId) {
      this.currentVariant = null;
      this.updateStickyCart();
    } else {
      const variantData = this.getVariantFromSwatches(variantId);
      if (variantData) {
        this.currentVariant = variantData;
        this.updateStickyCart();
      }
    }
  }

  private showStickyCart() {
    this.container.classList.add(CLASS_VISIBLE);
  }

  private hideStickyCart() {
    this.container.classList.remove(CLASS_VISIBLE);
  }

  private checkStickyCartForDesktop() {
    // On desktop with sticky details, only show sticky cart if:
    // 1. The gallery section is completely out of view, OR
    // 2. The user has scrolled significantly past the main form
    const gallerySection = document.querySelector('.c-product-main__gallery');
    const detailsSection = document.querySelector('.c-product-main__details');
    
    if (!gallerySection || !detailsSection) {
      this.showStickyCart();
      return;
    }

    const galleryRect = gallerySection.getBoundingClientRect();
    const detailsRect = detailsSection.getBoundingClientRect();
    
    const isGalleryOutOfView = galleryRect.bottom < 0;
    
    const scrollBuffer = 200;
    const hasScrolledPastForm = window.scrollY > (this.mainFormBottom + scrollBuffer);
    
    if (isGalleryOutOfView || hasScrolledPastForm) {
      this.showStickyCart();
    } else {
      this.hideStickyCart();
    }
  }

  private onScroll = () => {
    // Hide sticky cart when footer is visible
    if (this.shouldHideForFooter()) {
      this.hideStickyCart();
    }
  }

  private onResize = () => {
    // Check footer visibility on resize
    if (this.shouldHideForFooter()) {
      this.hideStickyCart();
    }
  }

  private shouldHideForFooter(): boolean {
    if (!this.footerElement) return false;

    const footerRect = this.footerElement.getBoundingClientRect();
    const stickyHeight = this.container.offsetHeight || 0;
    const footerInView = footerRect.top <= window.innerHeight - stickyHeight;

    return footerInView;
  }

  private onVariantChange = () => {
    
    if (!this.productForm) {
      return;
    }

    const variantSelector = this.productForm.querySelector('[name="id"]') as HTMLInputElement;
    if (!variantSelector) {
      return;
    }

    const variantId = parseInt(variantSelector.value);
    
    if (!variantId) {
      this.currentVariant = null;
      this.updateStickyCart();
      return;
    }

    const variantData = this.getVariantFromSwatches(variantId);
    
    if (!variantData) {
      return;
    }

    this.currentVariant = variantData;
    this.updateStickyCart();
  }

  private getVariantFromSwatches(variantId: number): Variant | null {
    if (this.sizeSelector) {
      const options = this.sizeSelector.querySelectorAll('option[data-variant-id]');
      for (const option of options) {
        const optionVariantId = parseInt(option.getAttribute('data-variant-id') || '0');
        if (optionVariantId === variantId) {
          const price = parseInt(option.getAttribute('data-variant-price') || '0');
          const comparePrice = option.getAttribute('data-variant-compare-price');
          const available = option.getAttribute('data-variant-available') === 'true';
          const inventoryQuantity = parseInt(option.getAttribute('data-variant-inventory-quantity') || '0');
          const inventoryPolicy = option.getAttribute('data-variant-inventory-policy') || 'deny';
          const inventoryManagement = option.getAttribute('data-variant-inventory-management') || 'shopify';

          return {
            id: variantId,
            price,
            compare_at_price: comparePrice ? parseInt(comparePrice) : null,
            available,
            inventory_quantity: inventoryQuantity,
            inventory_policy: inventoryPolicy,
            inventory_management: inventoryManagement,
          } as Variant;
        }
      }
    }

    const productForm = document.querySelector('[data-product-form]');
    if (!productForm) return null;

    const productDataScript = productForm.querySelector('script[type="application/json"]');
    if (productDataScript) {
      try {
        const productData = JSON.parse(productDataScript.textContent || '{}');
        const variant = productData.variants?.find((v: any) => v.id === variantId);
        if (variant) {
          return variant as Variant;
        }
      } catch (e) {
        console.warn('Failed to parse product data:', e);
      }
    }

    return null;
  }

  private updateStickyCart() {
    this.updatePrice();
    this.updateSizeSelector();
    this.updateAddToCartButton();
    this.updateNotifyButton();
    this.checkStickyCartVisibility();
  }

  private updatePrice() {
    if (!this.priceContainer || !this.currentVariant) return;

    const priceElement = this.priceContainer.querySelector('[data-product-money]');
    const comparePriceElement = this.priceContainer.querySelector('[data-product-compare-money]');

    if (priceElement) {
      priceElement.textContent = printMoney(this.currentVariant.price);
    }

    if (comparePriceElement) {
      if (this.currentVariant.compare_at_price) {
        comparePriceElement.textContent = printMoney(this.currentVariant.compare_at_price);
      } else {
        comparePriceElement.textContent = '';
      }
    }
  }

  private updateSizeSelector() {
    if (!this.sizeSelector) return;

    if (!this.currentVariant) {
      this.sizeSelector.value = '';
      return;
    }

    const options = this.sizeSelector.querySelectorAll('option[data-variant-id]');
    
    for (const option of options) {
      const optionElement = option as HTMLOptionElement;
      const optionVariantId = parseInt(optionElement.getAttribute('data-variant-id') || '0');
      
      if (optionVariantId === this.currentVariant.id) {
        this.sizeSelector.value = optionElement.value;
        break;
      }
    }
  }

  private updateAddToCartButton() {
    if (!this.addBtn) return;

    if (!this.currentVariant) {
      this.setButtonNoVariant();
      return;
    }

    const variant = this.currentVariant;
    const inCart = cartGetQuantityOfVariant(variant.id);

    if (variant.available) {
      if (
        variant.inventory_policy !== 'continue' &&
        variant.inventory_management !== null &&
        ((variant.inventory_quantity || 0) - inCart) <= 0
      ) {
        this.setButtonAllInCart();
      } else {
        this.setButtonAvailable();
      }
    } else {
      this.setButtonSoldOut();
    }
  }

  private setButtonNoVariant() {
    if (!this.addBtn) return;

    this.addBtn.textContent = t('products.product.add_to_cart');
    this.addBtn.classList.add(CLASS_DISABLED);
    this.addBtn.disabled = true;
    this.addBtn.style.display = 'block';
  }

  private setButtonAvailable() {
    if (!this.addBtn || !this.currentVariant) return;

    const price = printMoney(this.currentVariant.price);
    this.addBtn.textContent = `${t('products.product.add_to_cart')} - ${price}`;
    this.addBtn.classList.remove(CLASS_DISABLED);
    this.addBtn.removeAttribute('disabled');
    this.addBtn.disabled = false;
    this.addBtn.style.display = 'block';
  }

  private setButtonAllInCart() {
    if (!this.addBtn || !this.currentVariant) return;

    const price = printMoney(this.currentVariant.price);
    this.addBtn.classList.add(CLASS_DISABLED);
    this.addBtn.disabled = true;
    this.addBtn.textContent = `${t('products.product.all_in_cart')}`;
    this.addBtn.style.display = 'block';
  }

  private setButtonSoldOut() {
    if (!this.addBtn || !this.currentVariant) return;

    const price = printMoney(this.currentVariant.price);
    this.addBtn.classList.add(CLASS_DISABLED);
    this.addBtn.disabled = true;
    this.addBtn.textContent = `${t('products.product.sold_out')} - ${price}`;
    this.addBtn.style.display = 'none';
  }

  private updateNotifyButton() {
    if (!this.notifyBtn) return;

    if (!this.currentVariant) {
      this.notifyBtn.style.display = 'none';
      return;
    }

    if (this.currentVariant.available) {
      this.notifyBtn.style.display = 'none';
    } else {
      const price = printMoney(this.currentVariant.price);
      this.notifyBtn.textContent = `Notify Me - ${price}`;
      this.notifyBtn.style.display = 'block';
    }
  }

  private checkStickyCartVisibility() {
    if (!this.mainAddBtn) return;

    const isMainButtonHidden = this.mainAddBtn.style.display === 'none' || 
                              this.mainAddBtn.offsetHeight === 0;
    
    if (isMainButtonHidden && !this.hasSeenMainForm) {
      this.hasSeenMainForm = true;
      this.calculateMainFormBottom();
      this.showStickyCart();
    }
  }

  private onSizeChange = () => {
    if (!this.sizeSelector || !this.productForm) return;

    const selectedOption = this.sizeSelector.options[this.sizeSelector.selectedIndex];
    if (!selectedOption) return;

    const variantId = selectedOption.getAttribute('data-variant-id');
    
    if (!variantId || variantId === '') {
      this.currentVariant = null;
      this.updateStickyCart();
      this.clearSwatchSelections();
      return;
    }

    const variantSelector = this.productForm.querySelector('[name="id"]') as HTMLInputElement;
    if (!variantSelector) return;

    const variantIdNum = parseInt(variantId);
    if (!isNaN(variantIdNum)) {
      this.setVariantIdInSwatches(variantIdNum);
    }
  }

  private setVariantIdInSwatches(variantId: number) {
    const productForm = this.productForm as any;
    
    if (productForm && productForm.swatches && typeof productForm.swatches.setVariantId === 'function') {
      productForm.swatches.setVariantId(variantId);
      return;
    }

    if ((window as any).swatchesInstance) {
      (window as any).swatchesInstance.setVariantId(variantId);
      return;
    }

    this.simulateSwatchClick(variantId);
  }

  private clearSwatchSelections() {
    const productForm = this.productForm as any;
    
    
    if (productForm && productForm.swatches && typeof productForm.swatches.setVariantId === 'function') {
      productForm.swatches.setVariantId(null);
      this.removeVariantFromUrl();
      this.updateSwatchTitleAndButton();
      return;
    }

    if ((window as any).swatchesInstance) {
      (window as any).swatchesInstance.setVariantId(null);
      this.removeVariantFromUrl();
      this.updateSwatchTitleAndButton();
      return;
    }

    this.manuallyClearSwatchSelections();
    this.removeVariantFromUrl();
    this.updateSwatchTitleAndButton();
  }

  private manuallyClearSwatchSelections() {
    if (!this.productForm) return;

    const swatchButtons = this.productForm.querySelectorAll('[data-swatch-object]');
    swatchButtons.forEach(button => {
      button.classList.remove('is-selected');
      const parent = button.parentElement;
      if (parent) {
        parent.classList.remove('is-selected');
      }
    });

    const swatchDropdowns = this.productForm.querySelectorAll('[data-swatch-dropdown]');
    swatchDropdowns.forEach(dropdown => {
      (dropdown as HTMLSelectElement).value = '';
    });

    const swatchValues = this.productForm.querySelectorAll('[data-swatch-value]');
    swatchValues.forEach(valueElement => {
      valueElement.textContent = '';
    });

    const variantSelector = this.productForm.querySelector('[name="id"]') as HTMLInputElement;
    if (variantSelector) {
      variantSelector.value = '';
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      variantSelector.dispatchEvent(changeEvent);
    }
  }

  private removeVariantFromUrl() {
    if (typeof (window as any).queryReplaceParam === 'function') {
      (window as any).queryReplaceParam('variant', null);
    } else {
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.toString());
    }
  }

  private updateSwatchTitleAndButton() {
    if (!this.productForm) return;

    const swatchTitles = this.productForm.querySelectorAll('[data-swatch-title]');
    swatchTitles.forEach(titleElement => {
      const optionName = titleElement.getAttribute('data-swatch-title');
      if (optionName && optionName.toLowerCase() === 'size') {
        titleElement.textContent = 'Select Size';
      }
    });

    const addToCartButton = this.productForm.querySelector('[data-product-form-add]') as HTMLButtonElement;
    if (addToCartButton) {
      addToCartButton.textContent = t('products.product.add_to_cart');
      addToCartButton.classList.add(CLASS_DISABLED);
      addToCartButton.disabled = true;
      addToCartButton.style.display = '';
      addToCartButton.classList.remove('is-hidden');
    }

    const addBtnWrapper = this.productForm.querySelector('[data-add-btn-wrapper]');
    if (addBtnWrapper) {
      const notifyButtons = addBtnWrapper.querySelectorAll('[data-notify]') as NodeListOf<HTMLButtonElement>;
      notifyButtons.forEach(notifyButton => {
        notifyButton.classList.remove(CLASS_ACTIVE);
        notifyButton.style.display = 'none';
      });
    }
  }

  private simulateSwatchClick(variantId: number) {
    const productDataScript = this.productForm?.querySelector('script[type="application/json"]');
    if (!productDataScript) return;

    try {
      const productData = JSON.parse(productDataScript.textContent || '{}');
      const variant = productData.variants?.find((v: any) => v.id === variantId);
      
      if (!variant) return;
      
      const swatchButtons = this.productForm?.querySelectorAll('[data-swatch-object]');
      if (!swatchButtons) return;

      for (const button of swatchButtons) {
        const swatch = button.closest('[data-swatch]');
        if (!swatch) continue;

        const position = parseInt(swatch.getAttribute('data-swatch-position') || '0');
        const value = button.getAttribute('data-value');
        
        if (position > 0 && value && variant.options[position - 1] === value) {
          (button as HTMLElement).click();
          return;
        }
      }

      this.setVariantIdDirectly(variantId);
      
    } catch (e) {
      this.setVariantIdDirectly(variantId);
    }
  }

  private setVariantIdDirectly(variantId: number) {
    const variantSelector = this.productForm?.querySelector('[name="id"]') as HTMLInputElement;
    if (!variantSelector) return;

    variantSelector.value = variantId.toString();
    
    const events = ['input', 'change', 'blur'];
    events.forEach(eventType => {
      const event = new Event(eventType, { bubbles: true, cancelable: true });
      variantSelector.dispatchEvent(event);
    });
  }

  private onAddToCart = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.adding || !this.productForm) return;

    if (!this.currentVariant) {
      alert('Please select a variant before adding to cart');
      return;
    }

    this.adding = true;
    this.setButtonPending();

    try {
      const qtySelector = this.productForm.querySelector('[data-product-form-qty]') as HTMLInputElement;
      const quantity = qtySelector ? parseInt(qtySelector.value) || 1 : 1;

      await cartAdd({
        items: [{
          id: this.currentVariant.id,
          quantity: quantity
        }]
      });

      this.setButtonAdded();
      
      setTimeout(() => {
        this.updateAddToCartButton();
      }, 2000);

    } catch (error) {
      console.error('Failed to add to cart:', error);
      this.updateAddToCartButton();
    }

    this.adding = false;
  }

  private setButtonPending() {
    if (!this.addBtn) return;
    this.addBtn.textContent = t('products.product.cart_pending');
    this.addBtn.disabled = true;
  }

  private setButtonAdded() {
    if (!this.addBtn) return;
    this.addBtn.textContent = t('products.product.added_to_cart');
    this.addBtn.classList.remove(CLASS_DISABLED);
    this.addBtn.disabled = false;
  }

  public dispose() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.productForm) {
      this.productForm.removeEventListener(EVT_VARIANT_CHANGED, this.onVariantChange);
    }

    if (this.sizeSelector) {
      this.sizeSelector.removeEventListener('change', this.onSizeChange);
    }

    if (this.addBtn) {
      this.addBtn.removeEventListener('click', this.onAddToCart);
    }

    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
  }
}
